import React, { useEffect, useState, useCallback } from "react";

import { css } from "@compiled/react";
import { debounce } from "lodash";
import { useIntl } from "react-intl";
import { components, DropdownIndicatorProps } from "react-select";
import AsyncSelect from "react-select/async";

import SearchIcon from "@atlaskit/icon/glyph/search";

import { Cross } from "@/libraries/wpl/foundations/src/";

interface Option {
    readonly value: string;
    readonly label: string;
}

const SearchBarStyles = css({
    "position": "relative",
    "bottom": 0,
    "margin": "20px",
    "width": "1280px",
    "maxWidth": "100%",
    "zIndex": 5,
    "display": "flex",
    "justifyContent": "center",

    ".search-contents": {
        "position": "relative",
        "display": "flex",
        "alignItems": "center",

        ".search-input": {
            "position": "relative",
            "display": "flex",
            "verticalAlign": "middle",
            "alignItems": "center",
            "height": "2.5rem",
            "width": "895px",
            "borderWidth": "1px",
            "borderStyle": "solid",
            "borderRadius": "25px 25px 25px 25px",
            "padding": "0 16px",
            "borderColor": "#F4F5F7",
            "backgroundColor": "#F4F5F7",
            "margin": "16px",

            ".navSearchBarContainer": {
                "width": "100%",

                ".navSearchBar__control": {
                    width: "100%",
                    background: "none",
                    borderWidth: "0",
                    alignSelf: "center",
                    flex: 1,
                    fontFamily: "inherit",
                    appearance: "none",
                    fontSize: "1rem",
                    fontWeight: "100",
                    fontStyle: "italic"
                },

                ".navSearchBar__control.navSearchBar__control--is-focused.navSearchBar__control--menu-is-open":
                    {
                        "border": "none",
                        "borderColor": 0,
                        "boxShadow": "none",

                        "&:hover, &:focus, &:focus-visible": {
                            border: "none",
                            borderColor: 0,
                            boxShadow: "none"
                        }
                    },

                ".navSearchBar__indicator-separator": {
                    display: "none"
                }
            }
        }
    },

    ".close-button": {
        color: "#44546F",
        backgroundColor: "transparent",
        insetInlineEnd: "10px",
        border: "none",
        insetBlockStart: 0,
        insetBlockEnd: 0,
        margin: "auto"
    }
});

// TODO: Remove this when api done.  This is test data
const testData = [
    { value: "/jira", label: "What is Agile?" },
    {
        value: "Is the Agile Manifesto Still a Thing?",
        label: "Is the Agile Manifesto Still a Thing?"
    },
    {
        value: "What is Scrum? [+ How to Start]",
        label: "What is Scrum? [+ How to Start]"
    },
    {
        value: "Scrum Sprints: Everything You Need to Know",
        label: "Scrum Sprints: Everything You Need to Know"
    },
    { value: "Sprint Planning", label: "Sprint Planning" },
    {
        value: "Four agile ceremonies, demystified",
        label: "Four agile ceremonies, demystified"
    },
    {
        value: "Product Backlog Explained [+ Examples]",
        label: "Product Backlog Explained [+ Examples]"
    },
    {
        value: "What is a sprint review? 3 steps to better sprint reviews",
        label: "What is a sprint review? 3 steps to better sprint reviews"
    },
    {
        value: "What is a Stand Up Meeting? [+ Expert Tips]",
        label: "What is a Stand Up Meeting? [+ Expert Tips]"
    },
    {
        value: "What is a Scrum Master? [Roles & Responsibilities]",
        label: "What is a Scrum Master? [Roles & Responsibilities]"
    },
    {
        value: "Agile retrospectives: Use the past to define the future",
        label: "Agile retrospectives: Use the past to define the future"
    },
    {
        value: "How to Manage Scrum Remote Teams",
        label: "How to Manage Scrum Remote Teams"
    },
    {
        value: "A Deep Dive into Scrum Team Roles",
        label: "A Deep Dive into Scrum Team Roles"
    }
];

const SearchBar = (props: {
    readonly setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const intl = useIntl();
    const [searchResults, setSearchResults] = useState<Option[]>([]);
    const [searchValue, setSearchValue] = useState("");

    const handleEscKeyEvent = useCallback((event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setSearchValue("");
            props.setIsOpenSearch(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent]);

    const DropdownIndicator = (props: DropdownIndicatorProps<Option>) =>
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props}>
                <SearchIcon
                    label={intl.formatMessage({
                        id: "globalNav.search.label",
                        defaultMessage: "Search"
                    })}
                />
            </components.DropdownIndicator>
        );

    // TODO: Api call goes here. Need to update this
    const fetchOptions = (inputValue: string) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                console.log(inputValue);
                resolve(testData);
            }, 100);
        });
    };

    const searchApiCall = (
        inputValue: string,
        callback: (options: Option[]) => void
    ) => {
        setSearchValue(inputValue);
        debounce(() => {
            fetchOptions(inputValue).then((results) => {
                setSearchResults(testData);
                callback(results as Option[]);
            });
        }, 500)();
    };

    const noOptionsMessage = (obj: { inputValue: string }) => {
        if (obj.inputValue.trim().length === 0) {
            return null;
        }
        return "No results found";
    };

    // TODO: redirect to selected link goes here
    const onChangeHandler = (value: string) => {
        console.log(
            "this is the selected Option",
            value,
            searchResults,
            searchValue
        );
    };

    return (
        <div className="search-bar" css={SearchBarStyles}>
            <div className="search-contents">
                <div className="search-input">
                    <AsyncSelect<Option>
                        className="navSearchBarContainer"
                        classNamePrefix="navSearchBar"
                        onChange={(selectedOption) => {
                            onChangeHandler?.(selectedOption?.value || "");
                        }}
                        isClearable={true}
                        noOptionsMessage={noOptionsMessage}
                        placeholder=""
                        components={{
                            DropdownIndicator
                        }}
                        loadOptions={searchApiCall}
                        cacheOptions={true}
                        defaultOptions={false}
                        aria-label={intl.formatMessage({
                            defaultMessage: "Navbar search field",
                            id: "globalNav.searchBar"
                        })}
                    />
                </div>
            </div>

            <button
                className="close-button close-search"
                title="Close search"
                onClick={(e) => {
                    e.preventDefault();
                    setSearchValue("");
                    props.setIsOpenSearch(false);
                }}
            >
                <Cross size="24" />
            </button>
        </div>
    );
};

export default SearchBar;
